/* dev-only SPA navigation diagnostics */
import type { Router } from 'vue-router';

export function installSpaDebug(router: Router) {
  if ((window as any).__spaDebugInstalled) return;
  (window as any).__spaDebugInstalled = true;

  // Router guards
  let navStart = 0;
  router.beforeEach((to, from, next) => {
    navStart = performance.now();
    console.debug('[router] ->', from.fullPath, '=>', to.fullPath, { state: history.state });
    next();
  });
  router.afterEach((to, from, failure) => {
    const dt = Math.round(performance.now() - navStart);
    if (failure) {
      console.warn('[router] nav failed', to.fullPath, failure);
    } else {
      console.debug('[router] done', to.fullPath, 'in', dt, 'ms');
    }
  });
  router.onError((err, to) => {
    console.error('[router] error navigating to', to?.fullPath, err);
  });

  // Detect full reloads on load
  const navs = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const t = navs[0]?.type;
  console.info('[nav] navigation type:', t); // 'reload' => full page reload

  // Mark when a reload is about to happen
  window.addEventListener('beforeunload', () => {
    try {
      sessionStorage.setItem('lastBeforeUnload', new Date().toISOString());
    } catch {}
  });

  // Warn on same-origin raw anchors (bypass router)
  const clickHandler = (e: Event) => {
    const el = (e.target as HTMLElement)?.closest('a[href]') as HTMLAnchorElement | null;
    if (!el) return;
    const url = new URL(el.href, window.location.origin);
    const sameOrigin = url.origin === window.location.origin;
    const isHashOnly = url.pathname === window.location.pathname && url.hash;
    const routerLike = el.hasAttribute('data-router-link') || el.getAttribute('role') === 'link';
    if (sameOrigin && !isHashOnly && !el.hasAttribute('download') && el.target !== '_blank' && !routerLike) {
      console.warn('[anchor] raw anchor click (will cause full reload):', el.outerHTML);
      console.warn('[anchor] from:', getNodePath(el));
      console.warn('[anchor] stack:', new Error().stack);
    }
  };
  document.addEventListener('click', clickHandler, true);

  // Log history mutations
  const origPush = history.pushState;
  history.pushState = function (...args: any[]) {
    console.debug('[history.pushState]', args[2], { state: args[0] });
    return origPush.apply(this, args as any);
  };
  const origReplace = history.replaceState;
  history.replaceState = function (...args: any[]) {
    console.debug('[history.replaceState]', args[2], { state: args[0] });
    return origReplace.apply(this, args as any);
  };
  window.addEventListener('popstate', (e) => {
    console.debug('[history.popstate]', location.pathname + location.search + location.hash, { state: e.state });
  });

  // Global error visibility
  window.addEventListener('error', (e) => {
    console.error('[window.error]', e.error || e.message, e.filename, e.lineno, e.colno);
  });
  window.addEventListener('unhandledrejection', (e) => {
    console.error('[unhandledrejection]', e.reason);
  });

  function getNodePath(node: Element) {
    const parts: string[] = [];
    let el: Element | null = node;
    while (el && parts.length < 5) {
      let s = el.tagName.toLowerCase();
      if (el.id) s += '#' + el.id;
      if (el.classList?.length) s += '.' + [...(el.classList as any)].join('.');
      parts.unshift(s);
      el = el.parentElement;
    }
    return parts.join(' > ');
  }
}